import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Activity, Search } from "lucide-react";

import Panel from "../../components/Panel";
import WorkspacePageShell from "../../components/WorkspacePageShell";
import { businessApi } from "../../../services/api";
import { timeAgo } from "../../../utils/timeAgo";
import { useTimeRefresh } from "../../../hooks/useTimeRefresh";
import { getActivityActionText, getActivityCategoryText } from "../../../utils/activityLogText";
import { useBusinessAuth } from "../../context/BusinessAuthContext";

const MODULE_FILTERS = [
  { key: "all", label: "All", categories: [] },
  { key: "administration", label: "Administration", categories: ["BUSINESS_ADMINISTRATION"] },
  { key: "procurement", label: "Procurement", categories: ["BUSINESS_PROCUREMENT"] },
  { key: "inventory", label: "Inventory", categories: ["BUSINESS_INVENTORY"] },
  { key: "merchandising", label: "Merchandising", categories: ["BUSINESS_MERCHANDISING"] },
  { key: "orders", label: "Orders", categories: ["BUSINESS_ORDERS"] },
  { key: "finance", label: "Finance", categories: ["BUSINESS_FINANCE"] },
  { key: "hr", label: "HR", categories: ["BUSINESS_HR"] },
];

function actionBadge(action) {
  const base = "inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold";
  const normalized = String(action || "").toLowerCase();

  if (normalized.includes("approve") || normalized.includes("release")) {
    return `${base} border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200`;
  }
  if (normalized.includes("reject") || normalized.includes("cancel") || normalized.includes("decline")) {
    return `${base} border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200`;
  }
  if (normalized.includes("create") || normalized.includes("invite") || normalized.includes("submit")) {
    return `${base} border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-200`;
  }
  if (normalized.includes("update") || normalized.includes("adjust") || normalized.includes("record")) {
    return `${base} border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200`;
  }
  return `${base} border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300`;
}

function categoryBadge(category) {
  const base = "inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold";
  const normalized = String(category || "BUSINESS_ADMINISTRATION").toUpperCase();
  const map = {
    BUSINESS_ADMINISTRATION: `${base} border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-800/60 dark:bg-slate-950/40 dark:text-slate-200`,
    BUSINESS_PROCUREMENT: `${base} border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-200`,
    BUSINESS_INVENTORY: `${base} border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200`,
    BUSINESS_MERCHANDISING: `${base} border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-900/40 dark:bg-fuchsia-950/40 dark:text-fuchsia-200`,
    BUSINESS_ORDERS: `${base} border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200`,
    BUSINESS_FINANCE: `${base} border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200`,
    BUSINESS_HR: `${base} border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-900/40 dark:bg-violet-950/40 dark:text-violet-200`,
  };
  return map[normalized] || `${base} border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300`;
}

export default function AdministrationActivityLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useBusinessAuth();
  const authRole = String(auth?.role || "").toUpperCase();
  const canReadActivityLogs = authRole === "OWNER"
    || Boolean(auth?.hasPermission?.("activity_logs.read.business"))
    || Boolean(auth?.hasPermission?.("members:read"));
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useTimeRefresh(30_000);

  const requestedModuleKey = searchParams.get("module");
  const moduleKey = MODULE_FILTERS.some((item) => item.key === requestedModuleKey)
    ? requestedModuleKey
    : "all";
  const activeFilter = MODULE_FILTERS.find((item) => item.key === moduleKey) || MODULE_FILTERS[0];

  const handleModuleChange = useCallback((nextKey) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextKey && nextKey !== "all") next.set("module", nextKey);
      else next.delete("module");
      return next;
    });
  }, [setSearchParams]);

  const load = useCallback(async (page = 1) => {
    if (!canReadActivityLogs) {
      setLogs([]);
      setMeta(null);
      setError("");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        limit: 30,
      };
      if (activeFilter.categories.length) {
        params.categories = activeFilter.categories.join(",");
      }

      const res = await businessApi.getBusinessActivityLogs(params);
      setLogs(Array.isArray(res?.items) ? res.items : []);
      setMeta(res?.meta || null);
    } catch (err) {
      setError(err?.message || "Failed to load activity logs.");
      setLogs([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [activeFilter.categories, canReadActivityLogs]);

  useEffect(() => {
    load(1);
  }, [load]);

  const filteredLogs = useMemo(() => {
    if (!query.trim()) return logs;
    const needle = query.trim().toLowerCase();
    return logs.filter((log) =>
      `${log.member_name || ""} ${getActivityCategoryText(log)} ${getActivityActionText(log)} ${log.description || ""}`
        .toLowerCase()
        .includes(needle),
    );
  }, [logs, query]);

  return (
    <WorkspacePageShell
      eyebrow="Administration"
      title="Activity Logs"
      subtitle="Review business-side actor activity by module from the Administration workspace without collapsing it into the procurement process timeline."
    >
      <Panel
        title="Business Activity"
        subtitle="Track who did what, where, and when across procurement, inventory, merchandising, orders, finance, and HR."
        icon={Activity}
      >
        {!canReadActivityLogs ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
            You do not have permission to view business activity logs.
          </div>
        ) : (
          <>
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {MODULE_FILTERS.map((filter) => {
              const active = filter.key === moduleKey;
              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => handleModuleChange(filter.key)}
                  className={[
                    "rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                    active
                      ? "border-neutral-900 bg-neutral-900 text-white dark:border-neutral-100 dark:bg-neutral-100 dark:text-neutral-900"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search actor, action, or description..."
              className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-mnla-accent dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 sm:w-80"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-8 text-sm text-neutral-500 dark:text-neutral-400">Loading activity logs...</div>
        ) : error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
            {error}
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="py-8 text-sm text-neutral-500 dark:text-neutral-400">No activity logs found for the selected scope.</div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-2xl border border-neutral-200 dark:border-neutral-800">
              <table className="min-w-[760px] w-full text-left text-sm">
                <thead className="bg-neutral-50 dark:bg-neutral-900/60">
                  <tr className="border-b border-neutral-200 dark:border-neutral-800">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Actor</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Module</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Action</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Description</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="bg-white dark:bg-neutral-900">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{log.member_name || "Unknown"}</div>
                        {log.actor_role && (
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">{log.actor_role}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={categoryBadge(log.category)}>{getActivityCategoryText(log)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={actionBadge(log.action_type)}>{getActivityActionText(log)}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-600 dark:text-neutral-300">
                        {log.description || "-"}
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap text-neutral-500 dark:text-neutral-400">
                        {timeAgo(log.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && meta.totalPages > 1 ? (
              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  Page {meta.page} of {meta.totalPages} · {meta.total} total
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={meta.page <= 1}
                    onClick={() => load(meta.page - 1)}
                    className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 disabled:opacity-40 dark:border-neutral-700 dark:text-neutral-300"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => load(meta.page + 1)}
                    className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 disabled:opacity-40 dark:border-neutral-700 dark:text-neutral-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
          </>
        )}
      </Panel>
    </WorkspacePageShell>
  );
}
